import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const EMPTY = { name: '', description: '', price_per_m2: '', min_order_m2: '10', features: '', ideal_for: '', shade_tolerance: 'medium', drought_tolerance: 'medium', wear_tolerance: 'medium', maintenance: 'medium', active: true, in_stock: true, care_laying: '', care_watering: '', care_mowing: '' };
const LEVELS = ['low', 'medium', 'high'];
const TRAITS = [['shade_tolerance', 'Shade'], ['drought_tolerance', 'Drought'], ['wear_tolerance', 'Wear'], ['maintenance', 'Maintenance']];

export function VarietyFormDialog({ open, onOpenChange, variety, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(variety ? { ...EMPTY, ...variety, price_per_m2: String(variety.price_per_m2), min_order_m2: String(variety.min_order_m2), features: (variety.features || []).join('\n') } : EMPTY);
  }, [open, variety]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        name: form.name, description: form.description || null,
        price_per_m2: parseFloat(form.price_per_m2), min_order_m2: parseFloat(form.min_order_m2) || 0,
        features: form.features.split('\n').map((s) => s.trim()).filter(Boolean),
        ideal_for: form.ideal_for || null,
        shade_tolerance: form.shade_tolerance, drought_tolerance: form.drought_tolerance, wear_tolerance: form.wear_tolerance, maintenance: form.maintenance,
        active: form.active, in_stock: form.in_stock,
        care_laying: form.care_laying || null, care_watering: form.care_watering || null, care_mowing: form.care_mowing || null,
      });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="variety-form-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">{variety ? 'Edit variety' : 'Add turf variety'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-2 sm:col-span-3"><Label>Name</Label><Input required value={form.name} onChange={set('name')} data-testid="variety-name-input" /></div>
            <div className="space-y-2"><Label>Price per m² (AUD)</Label><Input required type="number" step="0.01" min="0" value={form.price_per_m2} onChange={set('price_per_m2')} data-testid="variety-price-input" /></div>
            <div className="space-y-2"><Label>Minimum order (m²)</Label><Input type="number" step="1" min="0" value={form.min_order_m2} onChange={set('min_order_m2')} data-testid="variety-min-input" /></div>
            <div className="space-y-2"><Label>Ideal for</Label><Input value={form.ideal_for} onChange={set('ideal_for')} placeholder="Family lawns, shade" data-testid="variety-ideal-input" /></div>
          </div>
          <div className="space-y-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={set('description')} data-testid="variety-description-input" /></div>
          <div className="space-y-2"><Label>Features (one per line)</Label><Textarea rows={4} value={form.features} onChange={set('features')} placeholder={'Soft leaf\nShade tolerant'} data-testid="variety-features-input" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {TRAITS.map(([key, label]) => (
              <div key={key} className="space-y-2">
                <Label>{label}</Label>
                <Select value={form[key]} onValueChange={(v) => setForm((f) => ({ ...f, [key]: v }))}>
                  <SelectTrigger data-testid={`variety-${key}-select`}><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Lawn care guide <span className="text-muted-foreground font-normal">(one step per line — shown on the variety page and linked in order emails)</span></p>
            <div className="space-y-2"><Label>Laying</Label><Textarea rows={4} value={form.care_laying || ''} onChange={set('care_laying')} data-testid="variety-care-laying-input" /></div>
            <div className="space-y-2"><Label>Watering</Label><Textarea rows={4} value={form.care_watering || ''} onChange={set('care_watering')} data-testid="variety-care-watering-input" /></div>
            <div className="space-y-2"><Label>Mowing &amp; care</Label><Textarea rows={3} value={form.care_mowing || ''} onChange={set('care_mowing')} data-testid="variety-care-mowing-input" /></div>
          </div>
          <div className="flex flex-wrap gap-6 pt-2">
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="variety-active-switch" /> Show on website</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.in_stock} onCheckedChange={(v) => setForm((f) => ({ ...f, in_stock: v }))} data-testid="variety-instock-switch" /> In stock</label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 btn-primary" data-testid="variety-save-btn">{saving ? 'Saving…' : 'Save variety'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
